import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { ShieldCheck, Upload, Trash2, FileLock2 } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/api";

export default function SignatureSettings() {
  const [cfg, setCfg] = useState(null);
  const [password, setPassword] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const refresh = async () => { const { data } = await api.get("/signature"); setCfg(data); };
  useEffect(() => { refresh(); }, []);

  const upload = async () => {
    if (!file) return toast.error("Selecione o arquivo .pfx");
    if (!password) return toast.error("Informe a senha do certificado");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("password", password);
      await api.post("/signature/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Certificado digital cadastrado");
      setPassword(""); setFile(null);
      refresh();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
    setBusy(false);
  };

  const toggle = async (enabled) => {
    await api.patch("/signature", { enabled });
    toast.success(enabled ? "Assinatura ativada" : "Assinatura desativada");
    refresh();
  };

  const remove = async () => {
    if (!window.confirm("Remover o certificado digital cadastrado?")) return;
    await api.delete("/signature");
    toast.success("Certificado removido"); refresh();
  };

  const has = cfg && cfg.holder_name;

  return (
    <Card data-testid="signature-card">
      <CardContent className="p-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="eyebrow">Assinatura digital ICP-Brasil</div>
            <h3 className="font-display font-bold text-lg mt-1 flex items-center gap-2">
              <FileLock2 className="w-5 h-5 text-[var(--unigr-navy)]" />
              Certificado e-CPF / e-CNPJ
            </h3>
            <p className="text-sm text-slate-600 mt-1 max-w-2xl">
              Faça upload do seu certificado digital (.pfx ou .p12) para assinar os certificados emitidos com validade jurídica (PAdES, ICP-Brasil).
              O arquivo é armazenado de forma criptografada e nunca é exposto na API.
            </p>
          </div>
          {has && (
            <div className="flex items-center gap-2">
              <Switch checked={!!cfg.enabled} onCheckedChange={toggle} data-testid="signature-toggle" />
              <span className="text-sm">{cfg.enabled ? "Ativa" : "Desativada"}</span>
            </div>
          )}
        </div>

        {has ? (
          <div className="mt-5 grid sm:grid-cols-2 gap-4">
            <Field label="Titular" value={cfg.holder_name} />
            <Field label="Documento" value={cfg.doc ? `${cfg.doc_type}: ${cfg.doc}` : "—"} />
            <Field label="Emissor (AC)" value={cfg.issuer} mono />
            <Field label="Válido até" value={(cfg.not_valid_after || "").slice(0, 10)} />
            <Field label="Status" value={cfg.enabled ? "✓ Ativa para próximas emissões" : "Desativada"} tone={cfg.enabled ? "green" : "muted"} />
            <div className="sm:col-span-2 flex gap-2 mt-2">
              <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="signature-replace-btn">
                <Upload className="w-4 h-4 mr-2" />Substituir certificado
              </Button>
              <Button variant="ghost" onClick={remove} data-testid="signature-remove-btn">
                <Trash2 className="w-4 h-4 mr-2 text-rose-600" />Remover
              </Button>
            </div>
          </div>
        ) : null}

        {/* Upload form (always available for first upload or replace) */}
        <div className={"mt-5 border-t pt-5 " + (has ? "hidden" : "")} data-testid="signature-upload-form">
          <div className="grid sm:grid-cols-3 gap-3 items-end">
            <div>
              <Label>Arquivo .pfx / .p12</Label>
              <input ref={fileRef} type="file" accept=".pfx,.p12" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="signature-file" />
              <Button variant="outline" onClick={() => fileRef.current?.click()} className="w-full mt-1" data-testid="signature-pick-file">
                <Upload className="w-4 h-4 mr-2" />{file?.name || "Selecionar arquivo"}
              </Button>
            </div>
            <div>
              <Label>Senha do certificado</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="signature-password" />
            </div>
            <Button onClick={upload} disabled={busy} className="btn-navy" data-testid="signature-upload-btn">
              <ShieldCheck className="w-4 h-4 mr-2" />{busy ? "Enviando…" : "Cadastrar"}
            </Button>
          </div>
          <p className="text-xs text-slate-500 mt-3">
            A senha é criptografada com Fernet (AES-128 em modo CBC + HMAC). O arquivo .pfx fica no Object Storage com chave de acesso restrita ao backend.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

function Field({ label, value, mono, tone }) {
  const cls = tone === "green" ? "text-emerald-700 font-semibold" : tone === "muted" ? "text-slate-500" : "text-[var(--unigr-text)]";
  return (
    <div>
      <div className="text-xs text-slate-500 uppercase tracking-wide">{label}</div>
      <div className={"mt-1 " + cls + (mono ? " font-mono text-xs break-all" : " text-sm")}>{value || "—"}</div>
    </div>
  );
}
